import type { RedoMerchantRpcClient } from "@redotech/merchant-sdk/rpc/client";
import { useHandler } from "@redotech/react-util/hook";
import { useEscalationContext } from "./escalation-context";

/**
 * A single chat-history line for the createEscalation payload. The
 * backend formats this into the Intercom kickoff message. Agents
 * provide their own messages → ChatHistoryLine[] adapter.
 */
export interface ChatHistoryLine {
  id?: string;
  role: "system" | "user" | "assistant";
  content: string;
}

export interface UseCreateEscalationParams {
  merchantRpcClient: RedoMerchantRpcClient;
  /**
   * Adapter: produce the chat history payload the server should include
   * in the Intercom kickoff message. Called at escalation time, so the
   * latest messages are captured (closures around stale snapshots will
   * silently truncate).
   */
  buildChatHistory: () => ChatHistoryLine[];
}

export interface CreateEscalationArgs {
  triageThreadId: string;
  escalationReason?: string;
  issueCategory?: string;
}

/**
 * Wraps `merchantRpcClient.createEscalation` with the side effects that
 * every caller needs: pushing the new escalation onto EscalationContext
 * so the status banner / SSE subscription / user-reply routing all
 * pick it up. Returning the raw RPC result lets the caller act on the
 * `intercomConversationId` (e.g. analytics) without re-reading context.
 */
export function useCreateEscalation({
  merchantRpcClient,
  buildChatHistory,
}: UseCreateEscalationParams) {
  const { setEscalation } = useEscalationContext();

  return useHandler(async (args: CreateEscalationArgs) => {
    const result = await merchantRpcClient.createEscalation({
      triageThreadId: args.triageThreadId,
      chatHistory: buildChatHistory(),
      escalationReason: args.escalationReason,
      issueCategory: args.issueCategory,
    });

    setEscalation({
      escalationId: result.escalationId,
      intercomConversationId: result.intercomConversationId,
      noticeTopic: result.noticeTopic,
      status: "waiting",
      // Bind the escalation to the thread it was created against so
      // the layout-level watcher can route inbound human messages to
      // the right cached chat even after the merchant browses to a
      // different thread (which would otherwise overwrite the
      // floating `triageThreadId` setter).
      triageThreadId: args.triageThreadId,
    });

    return result;
  });
}
